import { ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { KeyboardEvent } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useVehicleLookupMode from "../../../hooks/useVehicleLookupMode";
import classNames from "../../../utils/classNames";
import { identifierMaxLength } from "../../../utils/vehicleIdentifier";

interface VehicleIdentifierInputProps {
  /** Uncontrolled: the react-hook-form registration for the field. */
  register?: UseFormRegisterReturn;
  /** Controlled alternative to `register`. */
  value?: string;
  onChange?: (value: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** Overrides the button's own disabled rule (`disabled || isLoading`). */
  submitDisabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  buttonTitle?: string;
  loadingTitle?: string;
  buttonAriaLabel?: string;
  submitOnEnter?: boolean;
  className?: string;
}

const PLATE_FONT = { fontFamily: '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif' };

/**
 * The "type a registration and fetch its details" box. Registration mode is the UK number-plate
 * input the search filters and compare modal each used to carry a copy of; VIN mode is a plain
 * mono-spaced field capped at 17 characters, because a chassis number is not a plate.
 */
export default function VehicleIdentifierInput({
  register,
  value,
  onChange,
  onSubmit,
  isLoading = false,
  disabled = false,
  submitDisabled,
  placeholder,
  ariaLabel,
  buttonTitle,
  loadingTitle,
  buttonAriaLabel,
  submitOnEnter = false,
  className = "",
}: VehicleIdentifierInputProps) {
  const { t } = useTranslation("common");
  const { identifierType, isVinMode, isAsync } = useVehicleLookupMode();

  const buttonDisabled = submitDisabled ?? (disabled || isLoading);

  const handleKeyDown = submitOnEnter
    ? (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onSubmit();
        }
      }
    : undefined;

  const inputProps = register
    ? register
    : { value: value ?? "", onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value.toUpperCase()) };

  return (
    <div className={className}>
      <div
        className={classNames(
          "flex items-stretch overflow-hidden rounded-md border-2 shadow-sm focus-within:ring-2 focus-within:ring-primary-500",
          isVinMode ? "border-slate-700" : "border-black"
        )}
      >
        {!isVinMode && (
          <span aria-hidden="true" className="flex w-6 items-center justify-center bg-blue-700 text-[10px] font-bold uppercase tracking-widest text-yellow-300">
            {t("UK")}
          </span>
        )}
        <input
          type="text"
          {...inputProps}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={isVinMode ? t("ENTER VIN") : placeholder}
          aria-label={isVinMode ? t("VIN") : ariaLabel}
          maxLength={identifierMaxLength(identifierType)}
          className={
            isVinMode
              ? "w-full border-0 bg-white px-3 py-1 text-center font-mono text-lg font-semibold uppercase tracking-[0.2em] text-slate-900 placeholder:text-sm placeholder:tracking-[0.1em] placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:opacity-60"
              : "w-full border-0 bg-yellow-300 px-3 py-1 text-center text-xl font-bold uppercase tracking-[0.25em] text-black placeholder:text-sm placeholder:tracking-[0.15em] placeholder:text-black/40 focus:outline-none focus:ring-0 disabled:opacity-60"
          }
          style={isVinMode ? undefined : PLATE_FONT}
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={buttonDisabled}
          title={isLoading ? loadingTitle : buttonTitle}
          aria-label={buttonAriaLabel ?? buttonTitle}
          className={classNames(
            "flex w-9 items-center justify-center transition-colors",
            isVinMode ? "bg-slate-700 text-white" : "bg-black text-yellow-300",
            buttonDisabled ? "cursor-not-allowed opacity-60" : isVinMode ? "cursor-pointer hover:bg-slate-600" : "cursor-pointer hover:bg-slate-800"
          )}
        >
          {isLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" /> : <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>
      {isLoading && isAsync && <p className="mt-1 text-xs text-slate-500">{t("Looking up vehicle, this can take a few seconds…")}</p>}
    </div>
  );
}
